import { api } from './api';

export type MembershipRole =
  | 'customer_admin'
  | 'ehs_manager'
  | 'operations_supervisor'
  | 'external_reviewer'
  | 'product_evaluator'
  | 'sandbox_ehs_evaluator'
  | 'technical_integrator';
export type ProductProfile = 'operations' | 'setup' | 'acceptance' | 'admin';

export interface RbacMembership {
  id: string;
  principal_type: string;
  principal_id: string;
  org_id: string;
  site_id?: string | null;
  role: MembershipRole | string;
  primary_profile?: ProductProfile | string | null;
  secondary_profiles?: string[];
  created_at?: string | null;
}

export async function listMemberships(filters: {
  principal_type?: string;
  principal_id?: string;
  org_id?: string;
  site_id?: string;
} = {}): Promise<RbacMembership[]> {
  const response = await api.get('/api/v1/admin/memberships', { params: filters });
  return Array.isArray(response.data?.memberships) ? response.data.memberships : [];
}

/** Successful POST /admin/memberships (201). */
export type CreateMembershipResponse = {
  id: string;
};

/** 409 when a different role is requested for an existing org/site scope (API does not stack). */
export type MembershipScopeConflictBody = {
  error: 'membership_scope_conflict';
  detail?: string;
  requested_role?: string;
  existing?: {
    id: string;
    role: string | null;
    principal_type: string;
    principal_id: string;
    org_id: string;
    site_id?: string | null;
  };
};

export async function createMembership(payload: {
  principal_type: string;
  principal_id: string;
  org_id: string;
  site_id?: string;
  role: MembershipRole;
  primary_profile?: ProductProfile | null;
  secondary_profiles?: ProductProfile[];
}): Promise<CreateMembershipResponse> {
  const response = await api.post('/api/v1/admin/memberships', payload);
  return response.data;
}

export async function updateMembership(
  membershipId: string,
  payload: {
    role?: MembershipRole;
    primary_profile?: ProductProfile | null;
    secondary_profiles?: ProductProfile[];
  }
): Promise<RbacMembership> {
  const response = await api.patch(`/api/v1/admin/memberships/${membershipId}`, payload);
  return response.data?.membership as RbacMembership;
}

export async function deleteMembership(membershipId: string): Promise<void> {
  await api.delete(`/api/v1/admin/memberships/${membershipId}`);
}
